<template>
    <div
        class="field position--relative"
        :class="{ 'field--errors': validIfIsAnError }">
        <base-label
            v-if="settings.input.type !== 'checkbox' && settings.label"
            :settings="{ ...getLabelProps }"
        />
        <template v-if="settings.allowFilling || showComponent">
            <component
                :is="inputComponent"
                :id="settings.input.id"
                :name="settings.input.name"
                :type="settings.input.type"
                :placeholder="settings.input.placeholder"
                :autocomplete="settings.input.browserAutocomplete"
                :reset="settings.reset"
                :mask="settings.input.mask"
                :resetOptions="settings.resetOptions"
                :searchField="settings.input.searchField"
                :isCampaignList="settings.isCampaignList"
                :formatsAllowed="settings.input.formatsAllowed"
                v-bind="{
                    ...( settings.input.class && {
                        class: `field__input field__input--base ${settings.input.class}`
                    }),
                    ...( settings.input.targetClass && {
                        targetClass: `field__input field__input--base ${settings.input.targetClass}`
                    }),
                    ...( settings.input.value && { defaultValue: settings.input.value }),
                    ...( !settings.input.noListener && { 'data-listener': true }),
                    ...( ( showComponent && !settings.allowFilling ) && { allowFilling: false }),
                    ...settings.input.dynamicData
                }"
                v-model="value"
                @input-value="handleValue"
            />
            <base-label
                v-if="settings.input.type === 'checkbox' && settings.label"
                :settings="{
                    ...getLabelProps,
                    showIcon: true
                }"
            />
            <div
                v-if="validIfIsAnError"
                class="field--errors__container"
            >
                <icon-alert
                    :class="[
                        'field--errors__alert position--absolute',
                        defineAlertClass( 'alert' )
                    ]"
                />
                <div
                    :class="[
                        'field--errors__tooltip position--absolute background--red color--white capitalize',
                        defineAlertClass( 'tooltip' )
                    ]"
                >
                    {{ getWarnings }}
                </div>
            </div>
        </template>
        <template v-else>
            <div
                class="'field__input--base field__input--disabled display--flex'"
                :class="[
                    ( !settings.input.disableClass && 'field__input--disabled--default' ),
                    ( settings.input.disableClass && settings.input.disableClass )
                ]"
            >
                {{ getValue }}
            </div>
        </template>
    </div>
</template>

<script>
// i18n
import i18n from '@/plugins/i18n';

// Composition API
import {
    watch,
    computed,
    reactive,
    toRefs
} from '@vue/composition-api';

// Vuelidate
import useVuelidate from '@vuelidate/core';

// hooks
import { compareValues } from '@/hooks/use-input-listeners';
import { useActions } from '@/hooks/use-store';

export default {
    components: {
        BaseLabel: () => import( /* webpackChunkName: 'IconAlert' */ '@/components/bundles/fields/blocks/Label' ),
        IconAlert: () => import( /* webpackChunkName: 'IconAlert' */ '@/components/bundles/layout/images/icons/IconAlert' )
    },
    props: {
        settings: Object
    },
    setup ( props, { emit }) {
        const { actions } = useActions();
        const v$ = useVuelidate();

        const state = reactive({
            inputComponent: null,
            validator: {},
            validationParameter: props.settings.input.validationParameter,
            value: props.settings.input.value // assim é evitado que entre no watch.
        });

        const getIsValid = async () => { // executa as validações do campo.
            // caso precise para resetar - v$.$reset()
            v$.value.$touch(); // executa as validações.
            await validations(); // espera que termina as validações.
            return Promise.resolve( !v$.value.$error ); // returna se passou ou não nas validações
        };

        const validations = () => { // verifica se as validações já terminaram.
            // eslint-disable-next-line
            return new Promise( resolve => {
                // valida se existe erro e
                // se não esta pendente de outra validação
                //    caso sim, então não é necessário esperar.
                //    caso não, fica a escuta que a validação pendente termine.
                if ( v$.value.$error || !v$.value.$pending )
                    return resolve();

                // de 200mls irá validar se a validação está pendente.
                const listener = setInterval( () => {
                    if ( !v$.value.$pending ) {
                        clearInterval( listener );
                        resolve();
                    }
                }, 200 );
            });
        };

        const handleValue = async newValue => {
            state.value = newValue;
            const inputName = props.settings.input.name;

            // caso anteriormente exista um erro
            // associado ao campo, é limpo.
            switch ( true ) {
                case props.settings.input.isMissing:
                    actions( 'resetError', { type: 'missing', fieldName: inputName });
                    break;
                case props.settings.input.errorOnSubmit?.show:
                    actions( 'resetError', { type: 'api', fieldName: inputName });
                    break;
                default:
            }
            // valida se o utilizador esta a editar e
            // se o campo editado é igual ao valor guardado na BD ou
            // se o campo está vázio.
            //    caso não seja, executa as restantes validações.
            //    caso seja, não é necessário executar as restantes validações e
            //               remove o campo da store.
            if (
                ( !props.settings.isEdition && !newValue )
                || ( props.settings.isEdition && compareValues( newValue, props.settings.input.value ) )
            ) {
                actions( 'deleteField', { name: inputName });
                return;
            }

            // executa as validações ao campo.
            const isValid = await getIsValid();

            // valida se passou em todas as validações.
            //    caso tenha passado, vai guardar o campo em store,
            //                        de forma a dps submeter para a API.
            //    caso não tenha passado, vai remover o campo da store,
            //                            caso ele já esteja armazenado lá.
            ( isValid )
                ? actions( 'registerField', { name: inputName, value: newValue })
                : actions( 'deleteField', { name: inputName });

            // caso seja necessário emitir para o pai
            // o valor, de forma a que seja feito
            // algum procedimento.
            if ( props.settings.emitToParent )
                emit( 'get-value', newValue );
        };

        const validIfIsAnError = computed( () => {
            // validação se existe algum erro no campo ( validações ou campo em falta ).
            return v$.value.$error || props.settings.input.isMissing || props.settings.input.errorOnSubmit?.show;
        });

        const getWarnings = computed( () => {
            if ( props.settings.input.isMissing && !v$.value.error )
                return i18n.t( 'error.required' );

            if ( props.settings.input.errorOnSubmit?.show )
                return props.settings.input.errorOnSubmit.messages[0];

            return props.settings.input.errors.find( ({ type }) => !v$.value[type] ).warning;
        });

        const getLabelProps = computed( () => {
            const { settings } = props;

            return {
                name: settings.input.name,
                class: settings.label.class,
                text: settings.label.text,
                isRequired: settings.input.isRequired,
                allowFilling: settings.allowFilling,
                ...( settings.label.isNecessaryPre && { isNecessaryPre: settings.label.isNecessaryPre })
            };
        });

        const showComponent = computed( () => {
            // se for um destes componentes, quando o campo está bloqueado é necessário
            // manter a importação do componente, de forma a manter o aspecto e não estar
            // a replicar e com mais if's.
            return [ 'InputMultiOptions', 'InputCheckbox', 'InputToggle' ].includes( props.settings.input.component );
        });

        const haveLabel = computed( () => {
            return !props.settings.label.class.includes( 'field__label--hide' );
        });

        const isCalendarInline = computed( () => {
            return props.settings.input.dynamicData?.isInline;
        });

        const getValue = computed( () => {
            const { input } = props.settings;
            return input.dynamicData?.apiValue?.label || input.value || '---';
        });

        const defineAlertClass = computed( () => {
            return type => (
                props.settings.alert
                    ? `field--errors__${type}--${props.settings.alert.class}`
                    : !isCalendarInline
                        ? haveLabel ? `field--errors__${type}--default` : `field--errors__${type}--no-label`
                        : `field--errors__${type}--calendar`
            );
        });

        watch( () => props.settings.input.validationParameter,
            newParam => {
                state.validationParameter = newParam;

                // caso o campo fique com erro,
                // é apagado o valor da store.
                if ( v$.$error )
                    actions( 'deleteField', { name: props.settings.input.name });
            });

        watch( () => props.settings.input.component,
            async newComponent => {
                // obtém dinamicamente o componente de input.
                state.inputComponent = () => import(
                    /* webpackChunkName: '[request]' */
                    `@/components/dynamic/inputs/${newComponent}`
                ).then( chunk => chunk.default || chunk );

                // valida se o campo tem validações
                //    caso tenha, faz a importação do ficheiro de validações
                if ( props.settings.input.fileValidation ) {
                    state.validator = await import(
                        /* webpackChunkName: '[request]' */
                        `@/validations/${props.settings.input.fileValidation}`
                    ).then( validator => validator.default );
                }
            }, {
                immediate: true,
                deep: false
            });

        return {
            v$,
            handleValue,
            ...toRefs( state ),
            getWarnings,
            validIfIsAnError,
            getLabelProps,
            showComponent,
            haveLabel,
            isCalendarInline,
            getValue,
            defineAlertClass
        };
    },
    validations () {
        return {
            value: {
                ...this.validator
            }
        };
    }
};
</script>

<style lang="scss">
    // =============================================================================
    // Variables
    // =============================================================================
    $input-class: '.field__input';
    $input-autocomplete-class: '.field__autocomplete';
    $input-calendar-class: '.field__calendar';
    $arrow-class: '.field__autocomplete__arrow';
    $eye-class: '.field--password__eye';
    $checkbox-label-class: '.field__checkbox';
    $checkbox-icon-class: '.field__checkbox__icon';
    $switch-label-class: '.field__toggle__label';
    $input-height: 40px;

    // =============================================================================
    // Style
    // =============================================================================
    .field {
        &--errors {
            #{$input-class} {
                background: rgba( $color: $color-red, $alpha: .2 );
                color: $color-red;
                @include placeholder {
                    color: $color-red;
                }
            }
            #{$input-autocomplete-class}::after {
                width: 70px;
                background: rgba( $color: $color-red, $alpha: .2 );
            }
            #{$input-calendar-class} {
                .vc-container {
                    color: $color-red;
                }
            }
            #{$arrow-class} {
                @include transform( translate( -25px, -50% ) );
            }
            & + #{$eye-class} {
                display: none;
            }
            &__alert {
                @include transform-position( false, 12px, false, false, translateY( -50% ) );
                z-index: 3;
                &--default {
                    top: 48px;
                }
                &--no-label {
                    top: 50%;
                }
                &--name, &--autocomplete {
                    bottom: 0;
                }
                &--calendar {
                    bottom: 9px;
                }
                &--textarea {
                    bottom: 10px;
                }
            }
            &__tooltip {
                right: 8px;
                &--default {
                    bottom: calc( 100% - 30px );
                }
                &--no-label {
                    bottom: 45px;
                }
                &--name, &--autocomplete {
                    bottom: 36px;
                }
                &--calendar {
                    bottom: 53%;
                }
                &--textarea {
                    bottom: 46px;;
                }
                max-width: 215px;
                @include typography-specs( 12px, 14px );
                padding: 5px 10px;
                @include rounded-corner( 5px );
                z-index: 2;
                &::before {
                    @include pseudo();
                    @include dimensions( 8px, 8px );
                    background: $color-red;
                    @include transform-position( false, 10px, -4px, false, rotate( 45deg ) );
                }
            }
        }
        &__label {
            margin-bottom: 5px;
            font-weight: 300;
            &--hide {
                display: block;
                height: 0;
                margin-bottom: 0;
                overflow: hidden;
            }
            &__pre {
                white-space: pre-wrap;
                font-family: inherit;
            }
        }
        &__input {
            caret-color: $color-blue;
            background-color: $color-grey-light;
            @include rounded-corner( 2px );
            @include placeholder {
                color: rgba( $color-grey, .5 );
                text-transform: capitalize;
            }
            &:-webkit-autofill {
                -webkit-box-shadow: 0 0 0 50px $color-grey-light inset;
                -webkit-text-fill-color: $color-grey;
            }
            &--default {
                height: $input-height;
            }
            &--search {
                background: $color-white;
            }
            &--base {
                width: 100%;
                @include typography-specs( 15px, 40px, 300 );
                padding-left: 15px;
            }
            &--disabled {
                align-items: center;
                @include rounded-corner( 19px );
                @include typography-specs( 15px, 40px, 300 );
                padding-left: 0;
                &--default {
                    height: auto;
                }
            }
            &--checkbox {
                @include dimensions( 20px );
                @include rounded-corner( 5px );
            }
        }
        &__autocomplete {
            &::after {
                @include pseudo();
                @include transform-position( 0, -1px, false, false );
                @include dimensions( 45px, $input-height );
                z-index: 3;
                @include transition( all .15s ease-in-out );
                will-change: width, background;
            }
            &__input {
                z-index: 3;
                background: $color-white;
            }
            &__arrow {
                @include transform-position( 50%, 15px, false, false, translateY( -50% ) );
                @include transition( transform .3s ease-in-out );
                will-change: transform;
                z-index: 4;
                &--active {
                    @include transform( rotate( 180deg ) translateY( 50% ) );
                }
            }
            &__options {
                background: $color-white;;
                top: 0;
                opacity: 0;
                @include transition( opacity .3s ease-in-out );
                will-change: opacity;
                pointer-events: none;
                &--active {
                    opacity: 1;
                    pointer-events: all;
                    z-index: 2;
                }
                &__content {
                    @include dimensions( 100%, 145px );
                }
            }
        }
        &__textarea {
            width: 100%;
            height: 150px;
            padding-top: 10px;
            resize: none;
            @include rounded-corner( 2px );
            &--default {
                height: 200px;
            }
            &--disable {
                padding: 15px;
            }
        }
        &__multi {
            &__input {
                z-index: 1;
                &--disable {
                    background: $color-white;
                }
            }
            &__options {
                top: (-$input-height);
                &__content {
                    @include dimensions( 100%, 170px );
                }
            }
        }
        &__drop {
            width: 100%;
            @include rounded-corner( 5px );
            padding: 50px 0 25px;
            &::before {
                @include pseudo();
                top: $input-height;
                @include dimensions( 100%, 1px );
                background: $color-grey;
            }
            &__option {
                min-height: 33px;
                line-height: 33px;
                padding-left: 15px;
                &:hover, &.js--active {
                    background: rgba( $color: $color-shadow-default, $alpha: .34 );
                }
            }
        }
        &__checkbox {
            padding-left: 25px;
            &__icon {
                @include transform-position( 9px, false, false, 0 );
                @include dimensions( 15px, 15px );
                @include rounded-corner( 50% );
                &--field {
                    top: 6px;
                }
                &::before, &::after {
                    @include pseudo();
                    @include transform-position( 0, 0, 0, 0 );
                    @include dimensions( 10px, 1px );
                    @include transition( background .4s ease-in-out, transform .3s ease-in-out );
                    will-change: background, transform;
                    margin: auto;
                }
            }
            &__input {
                &:checked + #{$checkbox-label-class} #{$checkbox-icon-class} {
                    &::before, &::after {
                        background: #93D89F;
                        @include transition( background .2s ease-in-out, transform .3s ease-in-out );
                        z-index: 1;
                    }
                    &::before {
                        @include transform( rotate( -45deg ) );
                    }
                    &::after {
                        @include transform( rotate( 45deg ) );
                    }
                }
            }
        }
        &__states {
            &__value {
                &:not(:last-child) {
                    margin-right: 15px;
                }
                &--inactive {
                    opacity: .4;
                    @include transition( opacity .3s ease-in-out );
                    will-change: opacity;
                    &:hover {
                        opacity: 1;
                    }
                }
            }
        }
        &__calendar{
            .vc-container {
                font-family: 'Roboto';
            }
            .vc-title,
            .vc-nav-item {
                text-transform: capitalize;
            }
            .vc-date-time {
                display: flex;
                @include position-grid( center, space-between );
                width: 100%;
            }
            .vc-date {
                order: 2;
                padding: 0 !important;
                margin: 0 !important;
            }
            .vc-time {
                span {
                    color: $color-blue;
                }
            }
            .vc-time-icon {
                display: none;
            }
            .vc-select {
                select {
                    background-color: $color-grey-light !important;
                    color: $color-blue !important;
                    @include rounded-corner( 26px !important );
                }
                .vc-select-arrow {
                    svg {
                        fill: $color-blue !important;
                    }
                }
            }
        }
        &__scroll {
            width: calc( 100% - 19px );
            overflow-y: auto;
        }
    }
</style>